import React, { useState, useEffect } from 'react';
import { fileAccessApi } from '../api/fileAccessApi';

interface SecureAttachmentProps {
  fileName: string;
  index: number;
  isImage: boolean;
  onImageClick?: (url: string) => void;
  getFileIcon: (fileName: string) => string;
}

export const SecureAttachment: React.FC<SecureAttachmentProps> = ({
  fileName,
  index,
  isImage,
  onImageClick,
  getFileIcon,
}) => {
  const [attachmentUrl, setAttachmentUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadSecureUrl = async () => {
      try {
        setIsLoading(true);
        setError('');

        // If it's already a complete URL, use it directly
        if (fileName.startsWith('http://') || fileName.startsWith('https://')) {
          setAttachmentUrl(fileName);
          return;
        }

        // If it's an S3 reference URL, get a pre-signed URL
        if (fileName.startsWith('s3://')) {
          const secureUrl = await fileAccessApi.getSecureFileUrl(fileName);
          setAttachmentUrl(secureUrl);
          return;
        }

        // For local files, use the API endpoint
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
        setAttachmentUrl(`${API_BASE_URL}/feedbacks/attachments/${fileName}`);
      } catch (error) {
        console.error('Error loading attachment URL:', error);
        setError('Failed to load file');
        setAttachmentUrl(fileName); // Fallback to original
      } finally {
        setIsLoading(false);
      }
    };

    loadSecureUrl();
  }, [fileName]);

  if (isLoading) {
    return (
      <div key={index} className="mt-2">
        <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div key={index} className="mt-2">
        <div className="w-24 h-24 bg-red-100 rounded-lg flex items-center justify-center">
          <span className="text-red-600 text-xs">Error</span>
        </div>
      </div>
    );
  }

  if (isImage) {
    return (
      <div key={index} className="mt-2">
        <img
          src={attachmentUrl}
          alt={fileName}
          className="max-w-xs max-h-48 rounded-lg border shadow-sm cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onImageClick?.(attachmentUrl)}
          onError={(e) => {
            // Fallback to download link if image fails to load
            e.currentTarget.style.display = 'none';
            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
            if (fallback) fallback.style.display = 'block';
          }}
        />
        <a
          href={attachmentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden bg-gray-100 text-gray-800 px-3 py-1 rounded-md text-sm hover:bg-gray-200 inline-flex items-center"
          download
        >
          {getFileIcon(fileName)} {fileName}
        </a>
      </div>
    );
  } else {
    return (
      <a
        key={index}
        href={attachmentUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-gray-100 text-gray-800 px-3 py-1 rounded-md text-sm hover:bg-gray-200 inline-flex items-center mr-2 mb-2"
        download
      >
        <span className="mr-1">{getFileIcon(fileName)}</span>
        {fileName}
      </a>
    );
  }
}; 